/**
 * Display helpers for a vendor return (debit note, D44). Formatting only: every
 * figure a return shows is worked out by the server (services/debitNote.service.js)
 * and arrives ready to print, so nothing here adds, multiplies or rounds money.
 */
import type { Tone } from '../../components/ui/tokens';
import type { GoodsState, ReturnableBatch, ReturnableBox } from '../../lib/debitNoteApi';

const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/**
 * The colour behind a goods state badge (VR-3). The label (GOODS_STATE_LABELS)
 * carries the meaning; this only reinforces what needs someone's attention.
 */
export const GOODS_STATE_TONES: Record<Exclude<GoodsState, null>, Tone> = {
  held: 'attention',
  write_off_requested: 'attention',
  written_off: 'negative',
  released: 'neutral',
  returned: 'neutral',
  restored: 'neutral',
};

/** A figure the server already rounded, followed by its unit: "3 CASE". */
function figureWithUnit(value: number, unit: string): string {
  return `${value} ${unit}`.trim();
}

/**
 * What a goods return may take from a line's batch, in the billed unit, one fact
 * per entry: "Batch: 3 CASE free", then what is spoken for when there is any.
 * Empty when the batch cannot be read by quantity (the line is blocked instead).
 */
export function batchFactsOf(batch: ReturnableBatch, unit: string): string[] {
  if (!batch.returnableByQuantity || batch.freeQuantity === null) return [];
  const facts = [`Batch: ${figureWithUnit(batch.freeQuantity, unit)} free`];
  if (batch.reservedQuantity) facts.push(`${figureWithUnit(batch.reservedQuantity, unit)} reserved for orders`);
  if (batch.heldElsewhereQuantity) {
    const on = batch.heldElsewhereOn.length ? batch.heldElsewhereOn.join(', ') : 'other returns';
    facts.push(`${figureWithUnit(batch.heldElsewhereQuantity, unit)} held for ${on}`);
  }
  return facts;
}

/** A weighed box's weight as a person reads it off the label: "14.40". */
export function formatBoxWeight(value: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(value);
}

/** Why a box cannot be ticked for this return, in a few words. null when it can. */
export function boxUnavailableReason(box: Pick<ReturnableBox, 'available' | 'status'>): string | null {
  if (box.available) return null;
  if (box.status === 'reserved') return 'being picked for a vessel';
  if (box.status === 'held') return 'held for another return';
  return 'nothing left in it';
}

/**
 * An amount in the return's own currency, to the cent. A vendor return is in its
 * invoice's currency (TT$ or US$), so no currency is ever assumed: until the
 * invoice is known the number is shown bare.
 */
export function formatDebitNoteMoney(value: number | null | undefined, currency?: string | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '--';
  const code = String(currency || '').trim().toUpperCase();
  if (!code) return new Intl.NumberFormat('en-US', TWO_DECIMALS).format(value);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, ...TWO_DECIMALS }).format(value);
  } catch {
    // Intl refuses a code it does not know; the code still shows, beside the number.
    return `${code} ${new Intl.NumberFormat('en-US', TWO_DECIMALS).format(value)}`;
  }
}

/** The parts of what apiClient throws read here: the server's message and a line refusal's `errors`. */
type ApiError = { message?: unknown; validationErrors?: unknown };

function asApiError(err: unknown): ApiError | null {
  return err && typeof err === 'object' ? (err as ApiError) : null;
}

/** The server's message, written for a person, or `fallback` when there is none. */
export function errorMessageOf(err: unknown, fallback: string): string {
  const message = asApiError(err)?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
}

/**
 * The invoice lines a refusal names. debitNote.service refuses a line with
 * `errors: [{ lineNumber, code }]`, which apiClient hands on as `validationErrors`.
 */
export function refusedLineNumbers(err: unknown): number[] {
  const list = asApiError(err)?.validationErrors;
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => (entry && typeof entry === 'object' ? (entry as { lineNumber?: unknown }).lineNumber : null))
    .filter((n): n is number => typeof n === 'number' && Number.isInteger(n));
}
